import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Tabs,
    Tab,
    Typography,
    Grid,
    Card,
    CardContent,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useOrganizationContext } from '../../../../context/organization_context/OrganizationContext';
import TaxSetupGuide from '../setup/TaxSetupGuide';
import { getStripeInvoiceSettings, updateStripeInvoiceSettings } from '../../../../repository/StripeSettingsDAO';
import { useToast } from '../../../../context/toast/ToastContext';
import { ToastType } from '../../../../context/toast/ToastContext';
import AppSidebar from '../../../../components/navbar/DesktopAppSidebar';
import { HexColorPicker } from 'react-colorful';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CloudFunctions } from '../../../../utils/enums/CloudFunctions';

const TabPanel = ({ children, value, index, ...other }) => (
    <div
        role="tabpanel"
        hidden={value !== index}
        id={`invoice-settings-tabpanel-${index}`}
        {...other}
    >
        {value === index && (
            <Box sx={{ p: 3 }}>
                {children}
            </Box>
        )}
    </div>
);

// Helper function to validate hex color
const isValidHexColor = (color) => {
    // Remove # prefix if exists
    const hex = color.startsWith('#') ? color.slice(1) : color;
    
    // Check if length is valid (3 or 6 characters for hex colors)
    return (hex.length === 6 || hex.length === 3) && 
           !isNaN(parseInt(hex, 16)) && 
           /^[0-9A-Fa-f]{3,6}$/.test(hex);
};

const InvoiceSettings = () => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [activeTab, setActiveTab] = useState(0);
    const { organizationStripeInfo } = useOrganizationContext();
    const [settings, setSettings] = useState({
        primaryColor: '#000000',
        secondaryColor: '#ffffff',
        defaultCurrency: 'usd',
        defaultFooter: '',
        defaultMemo: '',
        paymentTerms: 30,
        statementDescriptor: '',
        customFields: [],
        acceptedPaymentMethods: ['card']
    });
    const [loading, setLoading] = useState(true);
    const [showColorPicker, setShowColorPicker] = useState(null);

    useEffect(() => {
        const loadSettings = async () => {
            if (organizationStripeInfo?.stripe_account_id) {
                const result = await getStripeInvoiceSettings(organizationStripeInfo.stripe_account_id);
                if (result.success) {
                    setSettings(result.data);
                }
                setLoading(false);
            }
        };
        loadSettings();
    }, [organizationStripeInfo]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleSettingChange = (field, value) => {
        setSettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveSettings = async () => {
        // Validate hex colors before saving
        if (!isValidHexColor(settings.primaryColor) || !isValidHexColor(settings.secondaryColor)) {
            showToast(t(StringKeys.PLEASE_ENTER_VALID_HEX_COLORS) || "Please enter valid hex colors", ToastType.WARNING);
            return;
        }

        try {
            const result = await updateStripeInvoiceSettings(organizationStripeInfo.stripe_account_id, settings);
            if (result.success) {
                showToast(t(StringKeys.SUCCESS), ToastType.SUCCESS);
            } else {
                showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
            }
        } catch (error) {
            console.error('Error saving settings:', error);
            showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
        }
    };

    const handleSaveTaxSettings = async (taxSettings) => {
        setLoading(true);
        try {
            const functions = getFunctions();
            const configureTaxSettings = httpsCallable(functions, CloudFunctions.ConfigureStripeTaxSettings);
            
            const result = await configureTaxSettings({
                accountId: organizationStripeInfo.stripe_account_id,
                settings: {
                    ...taxSettings,
                    setupCompleted: true
                }
            });

            if (result.data.success) {
                showToast(t(StringKeys.TAX_SETUP_SUCCESS), ToastType.SUCCESS);
                setActiveTab(0); // Return to branding tab after successful save
            } else {
                showToast(t(StringKeys.TAX_SETUP_ERROR), ToastType.DANGER);
            }
        } catch (error) {
            console.error('Error configuring tax settings:', error);
            showToast(t(StringKeys.TAX_SETUP_ERROR), ToastType.DANGER);
        } finally {
            setLoading(false);
        }
    };

    return (
        <AppSidebar
            content={
                <Box sx={{ width: '100%' }}>
                    <Paper sx={{ width: '100%', mb: 2 }}>
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            indicatorColor="primary"
                            textColor="primary"
                        >
                            <Tab label={t(StringKeys.BRANDING)} />
                            <Tab label={t(StringKeys.PAYMENT_SETTINGS)} />
                            <Tab label={t(StringKeys.TAX_SETTINGS)} />
                        </Tabs>

                        {/* Branding Settings */}
                        <TabPanel value={activeTab} index={0}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                {t(StringKeys.BRANDING)}
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography gutterBottom>
                                                            {t(StringKeys.PRIMARY_COLOR)}
                                                        </Typography>
                                                        <Grid container spacing={2} alignItems="center">
                                                            <Grid item>
                                                                <Box
                                                                    onClick={() => setShowColorPicker('primary')}
                                                                    sx={{
                                                                        width: 100,
                                                                        height: 40,
                                                                        backgroundColor: settings.primaryColor,
                                                                        cursor: 'pointer',
                                                                        border: '1px solid #ccc'
                                                                    }}
                                                                />
                                                            </Grid>
                                                            <Grid item>
                                                                <TextField
                                                                    label={t(StringKeys.HEX_VALUE) || "Hex Value"}
                                                                    value={settings.primaryColor.replace('#', '')}
                                                                    onChange={(e) => {
                                                                        // Only accept valid hex characters and limit to 6 chars
                                                                        const validHexValue = e.target.value
                                                                            .replace(/[^0-9a-fA-F]/g, '')
                                                                            .substring(0, 6);
                                                                        
                                                                        // Ensure the value has a # at the beginning
                                                                        const value = `#${validHexValue}`;
                                                                        handleSettingChange('primaryColor', value);
                                                                    }}
                                                                    size="small"
                                                                    sx={{ width: 150 }}
                                                                    inputProps={{ maxLength: 6 }}
                                                                    InputProps={{
                                                                        startAdornment: <Box sx={{ mr: 1 }}>#</Box>
                                                                    }}
                                                                    helperText={
                                                                        settings.primaryColor.replace('#', '').length < 6 ? 
                                                                        t(StringKeys.ENTER_SIX_HEX_CHARS) || "Enter 6 hex chars (0-9, A-F)" : 
                                                                        " "
                                                                    }
                                                                    FormHelperTextProps={{ sx: { minHeight: '1.25rem' } }}
                                                                    error={settings.primaryColor.replace('#', '').length > 0 && !isValidHexColor(settings.primaryColor)}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                        {showColorPicker === 'primary' && (
                                                            <Box sx={{ position: 'absolute', zIndex: 2, mt: 1 }}>
                                                                <Box
                                                                    sx={{
                                                                        position: 'fixed',
                                                                        top: 0,
                                                                        right: 0,
                                                                        bottom: 0,
                                                                        left: 0
                                                                    }}
                                                                    onClick={() => setShowColorPicker(null)}
                                                                />
                                                                <HexColorPicker
                                                                    color={settings.primaryColor}
                                                                    onChange={(color) => handleSettingChange('primaryColor', color)}
                                                                />
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography gutterBottom>
                                                            {t(StringKeys.SECONDARY_COLOR)}
                                                        </Typography>
                                                        <Grid container spacing={2} alignItems="center">
                                                            <Grid item>
                                                                <Box
                                                                    onClick={() => setShowColorPicker('secondary')}
                                                                    sx={{
                                                                        width: 100,
                                                                        height: 40,
                                                                        backgroundColor: settings.secondaryColor,
                                                                        cursor: 'pointer',
                                                                        border: '1px solid #ccc'
                                                                    }}
                                                                />
                                                            </Grid>
                                                            <Grid item>
                                                                <TextField
                                                                    label={t(StringKeys.HEX_VALUE) || "Hex Value"}
                                                                    value={settings.secondaryColor.replace('#', '')}
                                                                    onChange={(e) => {
                                                                        // Only accept valid hex characters and limit to 6 chars
                                                                        const validHexValue = e.target.value
                                                                            .replace(/[^0-9a-fA-F]/g, '')
                                                                            .substring(0, 6);
                                                                        
                                                                        // Ensure the value has a # at the beginning
                                                                        const value = `#${validHexValue}`;
                                                                        handleSettingChange('secondaryColor', value);
                                                                    }}
                                                                    size="small"
                                                                    sx={{ width: 150 }}
                                                                    inputProps={{ maxLength: 6 }}
                                                                    InputProps={{
                                                                        startAdornment: <Box sx={{ mr: 1 }}>#</Box>
                                                                    }}
                                                                    helperText={
                                                                        settings.secondaryColor.replace('#', '').length < 6 ? 
                                                                        t(StringKeys.ENTER_SIX_HEX_CHARS) || "Enter 6 hex chars (0-9, A-F)" : 
                                                                        " "
                                                                    }
                                                                    FormHelperTextProps={{ sx: { minHeight: '1.25rem' } }}
                                                                    error={settings.secondaryColor.replace('#', '').length > 0 && !isValidHexColor(settings.secondaryColor)}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                        {showColorPicker === 'secondary' && (
                                                            <Box sx={{ position: 'absolute', zIndex: 2, mt: 1 }}>
                                                                <Box
                                                                    sx={{
                                                                        position: 'fixed',
                                                                        top: 0,
                                                                        right: 0,
                                                                        bottom: 0,
                                                                        left: 0
                                                                    }}
                                                                    onClick={() => setShowColorPicker(null)}
                                                                />
                                                                <HexColorPicker
                                                                    color={settings.secondaryColor}
                                                                    onChange={(color) => handleSettingChange('secondaryColor', color)}
                                                                />
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        label={t(StringKeys.DEFAULT_FOOTER)}
                                                        multiline
                                                        rows={4}
                                                        value={settings.defaultFooter}
                                                        onChange={(e) => handleSettingChange('defaultFooter', e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        label={t(StringKeys.DEFAULT_MEMO)}
                                                        multiline
                                                        rows={2}
                                                        value={settings.defaultMemo}
                                                        onChange={(e) => handleSettingChange('defaultMemo', e.target.value)}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </TabPanel>

                        {/* Payment Settings */}
                        <TabPanel value={activeTab} index={1}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                {t(StringKeys.PAYMENT_SETTINGS)}
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>{t(StringKeys.DEFAULT_CURRENCY)}</InputLabel>
                                                        <Select
                                                            value={settings.defaultCurrency}
                                                            onChange={(e) => handleSettingChange('defaultCurrency', e.target.value)}
                                                            label={t(StringKeys.DEFAULT_CURRENCY)}
                                                        >
                                                            <MenuItem value="usd">USD</MenuItem>
                                                            <MenuItem value="eur">EUR</MenuItem>
                                                            <MenuItem value="gbp">GBP</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        type="number"
                                                        label={t(StringKeys.PAYMENT_TERMS)}
                                                        value={settings.paymentTerms}
                                                        onChange={(e) => handleSettingChange('paymentTerms', e.target.value)}
                                                        InputProps={{ inputProps: { min: 0 } }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        label={t(StringKeys.STATEMENT_DESCRIPTOR)}
                                                        value={settings.statementDescriptor}
                                                        onChange={(e) => handleSettingChange('statementDescriptor', e.target.value)}
                                                        helperText={t(StringKeys.STATEMENT_DESCRIPTOR_HELP)}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </TabPanel>

                        {/* Tax Settings */}
                        <TabPanel value={activeTab} index={2}>
                            <TaxSetupGuide onSave={handleSaveTaxSettings} />
                        </TabPanel>
                    </Paper>

                    {/* Only show save button for branding and payment settings */}
                    {activeTab !== 2 && (
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                onClick={handleSaveSettings}
                                disabled={loading}
                            >
                                {t(StringKeys.SAVE)}
                            </Button>
                        </Box>
                    )}
                </Box>
            }
        />
    );
};

export default InvoiceSettings; 